import {createAsyncThunk} from "@reduxjs/toolkit";
import {prepareJsonPayload, getAuthHeader} from "../store/utils";
import {HOSTNAME} from "../configs/Domain";
import {deepObjectFromDotPath} from "../utils/tools";


async function batchFetchPortfolios(userId, portfolioIds) {
  try {
    const response = await fetch(`${HOSTNAME}/portfolios/list?ids=${portfolioIds.join(',')}`);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const portfolios = await response.json();
    return portfolios;
  } catch (error) {
    console.error('Failed to fetch portfolios in batches:', error);
    throw error;
  }
}

export const fetchPortfoliosInBatches = createAsyncThunk(
  'portfolios/fetchInBatches',
  async ({ userId, portfolioIds, userObjects, batchSize = 50 }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const existingPortfolios = state.portfolios.items || [];

      // Filter out portfolios that are already in the state
      const idsToFetch = portfolioIds.filter(id =>
        !existingPortfolios.some(portfolio => portfolio.id === id)
      );

      // If all portfolios are already loaded, return early with existing data
      if (idsToFetch.length === 0) {
        const relevantPortfolios = existingPortfolios.filter(portfolio =>
          portfolioIds.includes(portfolio.id)
        );
        return relevantPortfolios.map(portfolio => {
          const userDetails = userObjects?.find(obj => obj.id === portfolio.id) || {};
          return { ...portfolio, _user: userDetails };
        });
      }

      // Fetch only the missing portfolios in batches
      let fetchedPortfolios = [];
      for (let i = 0; i < idsToFetch.length; i += batchSize) {
        const batchIds = idsToFetch.slice(i, i + batchSize);
        const response = await batchFetchPortfolios(userId, batchIds);
        fetchedPortfolios = [...fetchedPortfolios, ...response];
      }

      // Combine existing and newly fetched portfolios
      const existingRelevantPortfolios = existingPortfolios.filter(portfolio =>
        portfolioIds.includes(portfolio.id)
      );

      const allPortfolios = [...existingRelevantPortfolios, ...fetchedPortfolios];

      // Add user details to all portfolios
      return allPortfolios.map(portfolio => {
        const userDetails = userObjects?.find(obj => obj.id === portfolio.id) || {};
        return { ...portfolio, _user: userDetails };
      });

    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


/**
 * Fetches the detailed data for a list of portfolios.
 *
 * --Note: Handles checking for existing data in the state and only fetching if needed
 */
export const fetchDetailedPortfolioObjects = createAsyncThunk(
  'portfolios/fetchDetailed',
  async (portfolioIds, { getState, rejectWithValue }) => {
    const state = getState();
    // Assuming `items` is an array, we need to filter based on the `detailsFetched` flag or its absence.
    const items = [...state.portfolios.items];
    const idsToFetch = portfolioIds.filter(id =>
      !items.some(item => item.id === id && item.detailsFetched && item.hasOwnProperty('symbols')));
    // Early return if all requested details are already fetched
    if (idsToFetch.length === 0) {
      return items.filter(item => portfolioIds.includes(item.id));
    }
    try {
      const queryString = idsToFetch.join(',');
      const response = await fetch(`${HOSTNAME}/portfolios/list/positions?ids=${queryString}`);
      if (!response.ok) throw new Error('Network response was not ok');
      const fetchedData = await response.json();
      return [...items, ...fetchedData.map(data => ({ ...data, detailsFetched: true }))];
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);


/**
 * Update the document form detail data i.e description/name/tags for a portfolio
 * !! Does NOT update the positions, use updatePortfolioPositions for that !!
 */
export const updatePortfolioDetails = createAsyncThunk(
  'portfolios/updatePortfolioDetails',
  async ({ portfolioId, data }, { rejectWithValue }) => {
      const response = await fetch(
          `${HOSTNAME}/portfolios/${portfolioId}/update/details`, prepareJsonPayload({data})
      );
      if (!response.ok) {
        throw new Error('Failed to update portfolio');
      }
      const responseData = await response.json();
      return { id: portfolioId, data: deepObjectFromDotPath(data) }
  }
);


/**
 *
 */
export const updateUserPortfolioData = createAsyncThunk(
  'portfolios/updateUserPortfolioData',
  async ({portfolioID, updateFields }, { rejectWithValue }) => {
    try {
      // Construct the request payload to generalize the update operation
      let update = {};
      for (let k in updateFields) {
        update[`portfolios_v2.$.${k}`] = updateFields[k];
      }
      const payload = {
        match: {
            uid: localStorage.getItem('uid'),
            'portfolios_v2.id': portfolioID
          }, // Matching the specific portfolio by ID
          update: {
            $set: update, // The update to apply
        }
      }
      const response = await fetch(`${HOSTNAME}/user/update`, prepareJsonPayload({data: payload}));
      if (!response.ok) throw new Error('Failed to update user portfolio data');
      const updatedData = await response.json();
      return updatedData;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const saveUserPortfolioSnapshot = createAsyncThunk(
    'portfolios/saveUserPortfolioSnapshot',
    async ({portfolioID, snapshot }, { rejectWithValue }) => {
      try {
        const response = await fetch(`${HOSTNAME}/user/signals/${portfolioID}/confirm`,
            prepareJsonPayload({
              data: {
                snapshot: snapshot
              }
            })
        )
        if (!response.ok) throw new Error('Failed to save user portfolio snapshot');
        const updatedData = await response.json();
        return {portfolioID, ...updatedData}
        } catch (error) {
        return rejectWithValue(error.toString());

      }
    }
);


export const createNewPortfolio = createAsyncThunk(
    'portfolios/createNewPortfolio',
    async ({title, type, itemArray, props, category}, { rejectWithValue }) => {
        try {
            const payload = {
                    name: title,
                    type: type,
                    ptfl: itemArray
            }
            if (props){
                payload.properties = props;
            }
            if (category){
                payload.category = category;
            }
            const response = await fetch(`${HOSTNAME}/portfolios/create`, prepareJsonPayload({
                data: payload
            }))
            if (!response.ok) throw new Error('Failed to create new portfolio');
            const data = await response.json();
            return data;
        }catch(e){

        }
    }
)

/**
 * Update the postions (holdings contents whatever) of a portfolio here
 */
export const updatePortfolioPositions = createAsyncThunk(
    'portfolios/updatePortfolioPositions',
    async ({portfolioId, itemArray}, { rejectWithValue }) => {
        try {
            const response = await fetch(`${HOSTNAME}/portfolios/${portfolioId}/update/positions`,
                prepareJsonPayload({
                data: {
                    ptfl: itemArray
                }}))
            if (!response.ok) throw new Error('Failed to create new portfolio');
            const data = await response.json();
                return { portfolioId, ptfl: data.ptfl, cash: data.cash }
        }catch(e){
            return rejectWithValue()
        }
    }
)

/**
 *
 */
export const deletePortfolios = createAsyncThunk(
    'portfolios/deletePortfolios',
    async ({portfolioIds}, { rejectWithValue }) => {
        try {
            const response = await fetch(`${HOSTNAME}/portfolios/delete`,
                prepareJsonPayload({
                    method: "DELETE",
                    data: {
                        ids: portfolioIds
                    }
                })
            )
            if (!response.ok) throw new Error('Failed to create new portfolio');
            const data = await response.json();
            return data;
        }catch(e){

        }
    }
)


/**
 * Fetches the history of a user's portfolio.
 * @param {Object} args - The arguments for the thunk.
 * @param {string} args.portfolioID - The ID of the portfolio to fetch history for.
 * @returns {Promise<Object>} The portfolio history data.
 */
export const fetchUserPortfolioHistory = createAsyncThunk(
  'portfolios/fetchUserPortfolioHistory',
  async ({ portfolioId, limit=-1 }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${HOSTNAME}/portfolios/${portfolioId}/user/history?limit=${limit}`, {
        method: 'GET',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch user portfolio history');
      const historyData = await response.json();
      return {portfolioId, historyData};
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

